import moment, { Moment } from 'moment';
import { capitalizeFirstLetter } from 'shared/helpers/string';
import { IOrderInvoice, OrderInvoice } from 'shared/types/orderInvoice';
import { ICustomer } from './customer';

const paymentCustomer = process.env.REACT_APP_CUSTOMER_PAYMENT;

export enum orderStatus {
  EM_ABERTO = 1,
  PAGO = 2,
  ACORDO = 3,
  CANCELADO = 4,
}

export interface IOrder {
  id?: number;
  contract_id?: number;
  bank_id: 1 | 2;
  invoices?: Array<IOrderInvoice> | IOrderInvoice;
  cycle?: number;
  complete_at?: string;
  codigo?: number;
  status_id?: orderStatus;
  token?: string;
  price?: number;
  due_date?: string;
  customer?: ICustomer;
  desc?: string;
  installments?: string;
  discount?: string;
  created_at?: string;
  cancelled_at?: string;
  nfse?: { id: number; access_key: string };
}

export class Order {
  id?: number;
  contractId?: number;
  bankId: 1 | 2;
  invoices: Array<OrderInvoice> = [];
  invoice?: OrderInvoice;
  cycle?: number;
  complete_at?: Moment;
  codigo: number;
  statusId?: orderStatus;
  token: string;
  price: number;
  due_date?: Moment;
  description?: string;
  installments?: string;
  discount?: number;
  created_at?: Moment;
  cancelled_at?: Moment;
  statusLabel?: string;
  nfse?: { id: number; accessKey: string };

  constructor(data: IOrder) {
    this.id = data.id;
    this.contractId = data.contract_id;
    this.bankId = data.bank_id;
    this.cycle = data.cycle;
    this.complete_at = moment(data.complete_at);
    this.codigo = data.codigo ?? 0;
    this.statusId = data.status_id;
    this.token = data.token ?? '';
    this.price = Number(data.price) ?? 0;
    this.due_date = moment(data.due_date);
    this.description = data.desc;
    this.installments = data.installments;
    if (data.discount) {
      this.discount = Number(data.discount);
    }
    if (data.created_at) {
      this.created_at = moment(data.created_at);
    }
    if (data.cancelled_at) {
      this.cancelled_at = moment(data.cancelled_at);
    }
    this.nfse = data.nfse
      ? { id: data.nfse.id, accessKey: data.nfse.access_key }
      : undefined;

    if (Array.isArray(data.invoices)) {
      this.invoices = data.invoices.map((inv) => new OrderInvoice(inv));
      if (this.invoices.length > 0) {
        const activeInvoice =
          this.invoices.find((i) => i.is_active) ?? this.invoices[0];
        this.invoice = activeInvoice;
      }
    } else if (data.invoices) {
      this.invoice = new OrderInvoice(data.invoices);
    }
  }

  get paymentLink(): string {
    if (!this.isInvoice) return `${paymentCustomer}contratacao/${this.token}`;
    return '';
  }

  get hasInvoiceLink(): boolean {
    return (
      this.statusId === 1 &&
      (this.invoices.some((invoice) => invoice.is_active) ||
        !!this.invoice?.is_active)
    );
  }

  get isInvoice(): boolean {
    return this.bankId === 2;
  }

  get canGenerateInvoice(): boolean {
    return (
      this.statusId === 1 &&
      this.isInvoice &&
      !!this.due_date &&
      this.due_date.diff(moment(), 'days') < 10 &&
      !this.hasInvoiceLink
    );
  }

  get dueDateMonth() {
    return capitalizeFirstLetter(this.due_date?.format('MMMM') as string);
  }

  get priceString() {
    return `R$ ${this.price.toFixed(2).replace('.', ',')}`;
  }

  get discountDisplay() {
    if (this.discount) {
      return `R$ ${this.discount.toFixed(2).replace('.', ',')}`;
    }
    return 'R$ 0,00';
  }
}
